import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import {
  Button,
  CopyableHash,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Pagination,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SkeletonTableRows,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  TruncatedText,
  useToast,
} from '@shared/ui';
import type { Withdrawal } from '@entities/withdrawal';
import {
  canApproveAdminWithdrawal,
  canCancelAdminWithdrawal,
  canForceAddressHold,
  canIssueEmergencyGrant,
  canRevokeEmergencyGrant,
  createIdempotencyKey,
  grantViewStatus,
  isTerminalWithdrawalState,
  merchantHintKey,
  WITHDRAWAL_STATES,
  withdrawalApprovalBlock,
} from '@entities/withdrawal';
import { ReconcileDialog } from '@features/withdrawal';
import type { AdminCommandDto, ApproveWithdrawalPayload, EmergencyOverride } from '@shared/api/contracts';
import { useEmergencyGrants } from '@features/withdrawal';
import { AdminCommandDialog } from '@features/withdrawal';
import { formatLedgerAmount } from '@shared/lib/format-ledger-amount';
import { formatDateTime } from '@shared/lib/format-date';
import { useCopyToClipboard } from '@shared/lib/use-copy-to-clipboard';
import { WithdrawalStatusBadge } from '@entities/withdrawal';
import { WithdrawalMeta } from '@entities/withdrawal';
import { PolicyDecisionsDialog } from './policy-decisions-dialog';

interface AdminWithdrawalsTableProps {
  withdrawals: Withdrawal[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  isLoading?: boolean;
  isRefreshing?: boolean;
  isActionLoading?: boolean;
  onRefresh?: () => void;
  onPageChange?: (page: number) => void;
  actorId?: string;
  canExecute?: boolean;
  canReject?: boolean;
  canEmergency?: boolean;
  canReadAudit?: boolean;
  onProcess?: (
    id: string,
    payload?: ApproveWithdrawalPayload,
  ) => Promise<{ success: boolean; error?: string }>;
  onCancel?: (id: string, comment: string) => Promise<{ success: boolean; error?: string }>;
  onCreateGrant?: (payload: {
    withdrawalId: string;
    overrides: EmergencyOverride[];
    idempotencyKey: string;
    command: AdminCommandDto;
  }) => Promise<{ success: boolean; error?: string }>;
  onRevokeGrant?: (payload: { id: string; command: AdminCommandDto }) => Promise<{ success: boolean; error?: string }>;
  stateFilter?: string;
  onStateFilter?: (state: string) => void;
  onReconcile?: (id: string, reason: string, expectedStateVersion: number) => Promise<{ success: boolean; error?: string }>;
  loadError?: boolean;
}

export function AdminWithdrawalsTable({
  withdrawals,
  pagination,
  isLoading = false,
  isRefreshing = false,
  isActionLoading = false,
  onRefresh,
  onPageChange,
  actorId,
  canExecute = false,
  canReject = false,
  canEmergency = false,
  canReadAudit = false,
  onProcess,
  onCancel,
  onCreateGrant,
  onRevokeGrant,
  stateFilter = '',
  onStateFilter,
  onReconcile,
  loadError = false,
}: AdminWithdrawalsTableProps) {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const { copy } = useCopyToClipboard();
  const showSkeleton = (isLoading && !withdrawals.length) || isRefreshing;
  const spinRefresh = isLoading || isRefreshing;
  const [processTarget, setProcessTarget] = useState<Withdrawal | null>(null);
  const [forceMode, setForceMode] = useState(false);
  const [policyTarget, setPolicyTarget] = useState<string | null>(null);
  const [emergencyTarget, setEmergencyTarget] = useState<Withdrawal | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Withdrawal | null>(null);
  const [cancelComment, setCancelComment] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [reconcileTarget, setReconcileTarget] = useState<Withdrawal | null>(null);

  const handleConfirmProcess = async (command: AdminCommandDto) => {
    if (!processTarget || !onProcess) return;
    if (forceMode && !command.reason.trim()) {
      toast(t('withdrawals.actions.forceReasonRequired'), 'error');
      return;
    }

    const result = await onProcess(processTarget.id, {
      secondFactor: command.secondFactor,
      ...(forceMode ? { forceCoolingPeriod: true, forceReason: command.reason.trim() } : {}),
    });
    if (result.success) {
      toast(t('withdrawals.actions.processSuccess'), 'success');
      setProcessTarget(null);
      setForceMode(false);
      onRefresh?.();
      return;
    }

    if (result.error) {
      toast(result.error, 'error');
    }
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget || !onCancel) return;

    if (!cancelComment.trim()) {
      setFormError(t('withdrawals.actions.commentRequired'));
      return;
    }

    setFormError(null);
    const result = await onCancel(cancelTarget.id, cancelComment.trim());
    if (result.success) {
      toast(t('withdrawals.actions.cancelSuccess'), 'success');
      setCancelTarget(null);
      setCancelComment('');
      onRefresh?.();
      return;
    }

    if (result.error) {
      toast(result.error, 'error');
    }
  };

  const openCancelDialog = (withdrawal: Withdrawal) => {
    setCancelComment('');
    setFormError(null);
    setCancelTarget(withdrawal);
  };

  return (
    <>
      <div className="surface-card">
        <div className="mb-3 flex flex-col gap-3 sm:mb-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="section-title">{t('withdrawals.adminTitle')}</h2>
          <div className="flex items-center gap-2">
            <Select value={stateFilter || 'all'} onValueChange={(value) => onStateFilter?.(value === 'all' ? '' : value)}>
              <SelectTrigger className="w-full sm:w-56" aria-label={t('withdrawals.filter.state')}>
                <SelectValue placeholder={t('withdrawals.filter.all')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('withdrawals.filter.all')}</SelectItem>
                {WITHDRAWAL_STATES.map((state) => (
                  <SelectItem key={state} value={state}>{t(`withdrawals.adminState.${state}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            className="rounded-full sm:size-10"
            loading={spinRefresh}
            leftIcon={<RefreshCw className="size-4" />}
            aria-label={t('common.retry', { ns: 'common' })}
            onClick={() => onRefresh?.()}
          />
          </div>
        </div>

        {showSkeleton ? (
          <SkeletonTableRows rows={6} className="gap-3" />
        ) : loadError && !withdrawals.length ? (
          <div className="space-y-3 py-12 text-center">
            <p className="font-sans text-sm text-muted-foreground">{t('withdrawals.loadError')}</p>
            <Button type="button" variant="outline" size="sm" onClick={() => onRefresh?.()}>
              {t('common.retry', { ns: 'common' })}
            </Button>
          </div>
        ) : !withdrawals.length ? (
          <div className="py-12 text-center font-sans text-xs text-muted-foreground sm:py-16 sm:text-sm">{t('withdrawals.empty')}</div>
        ) : (
          <>
            <div className="-mx-1 min-w-0 overflow-x-auto px-1">
              <Table className="table-compact min-w-[52rem]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">{t('withdrawals.columns.id')}</TableHead>
                    <TableHead className="min-w-[9rem] whitespace-nowrap text-right sm:min-w-[10.5rem]">
                      {t('withdrawals.columns.amount')}
                    </TableHead>
                    <TableHead>{t('withdrawals.columns.network')}</TableHead>
                    <TableHead>{t('withdrawals.columns.address')}</TableHead>
                    <TableHead>{t('withdrawals.columns.status')}</TableHead>
                    <TableHead>{t('withdrawals.columns.policy')}</TableHead>
                    <TableHead>{t('withdrawals.columns.date')}</TableHead>
                    <TableHead>{t('rbac:withdrawal.maker')}</TableHead>
                    <TableHead className="min-w-[13rem]">{t('withdrawals.columns.user')}</TableHead>
                    <TableHead className="w-44">{t('withdrawals.columns.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {withdrawals.map((withdrawal) => (
                    <TableRow key={withdrawal.id}>
                      <TableCell className="font-medium">{withdrawal.id}</TableCell>
                      <TableCell className="min-w-[9rem] whitespace-nowrap text-right font-medium tabular-nums sm:min-w-[10.5rem]">
                        {formatLedgerAmount(withdrawal.amount, { assetLabel: withdrawal.assetCode })}
                      </TableCell>
                      <TableCell className="w-[1%] whitespace-nowrap">{withdrawal.network}</TableCell>
                      <TableCell className="max-w-[10rem] sm:max-w-[12rem]">
                        <CopyableHash
                          value={withdrawal.address}
                          onCopy={() => copy(withdrawal.address, undefined, withdrawal.address)}
                        />
                      </TableCell>
                      <TableCell>
                        <WithdrawalStatusBadge status={withdrawal.status} state={withdrawal.state} audience="admin" />
                      </TableCell>
                      <TableCell>
                        <WithdrawalMeta withdrawal={withdrawal} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatDateTime(withdrawal.createdAt)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {t(`rbac:withdrawal.maker${withdrawal.makerKind === 'AUTO' ? 'Auto' : withdrawal.makerKind === 'B2B' ? 'B2b' : withdrawal.makerKind === 'HUMAN' ? 'Human' : 'Unknown'}`)}
                        {withdrawal.adminEmail ? (
                          <p className="text-xs text-muted-foreground">{t('rbac:withdrawal.confirmedBy', { email: withdrawal.adminEmail })}</p>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <div className="min-w-[240px]">
                          <TruncatedText
                            value={withdrawal.userEmail ?? 'N/A'}
                            className="max-w-[240px] font-medium"
                          />
                          {withdrawal.userId && (
                            <p className="text-xs text-muted-foreground">ID: {withdrawal.userId}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <WithdrawalRowActions
                          withdrawal={withdrawal}
                          actorId={actorId}
                          canExecute={canExecute}
                          canReject={canReject}
                          canEmergency={canEmergency && Boolean(onCreateGrant)}
                          canReadAudit={canReadAudit}
                          isActionLoading={isActionLoading}
                          onProcess={() => {
                            setForceMode(false);
                            setProcessTarget(withdrawal);
                          }}
                          onForce={() => {
                            setForceMode(true);
                            setProcessTarget(withdrawal);
                          }}
                          onPolicy={() => setPolicyTarget(withdrawal.id)}
                          onCancel={() => openCancelDialog(withdrawal)}
                          onEmergency={() => setEmergencyTarget(withdrawal)}
                          onReconcile={
                            canExecute && onReconcile && withdrawal.stateVersion != null && !isTerminalWithdrawalState(withdrawal.state)
                              ? () => setReconcileTarget(withdrawal)
                              : undefined
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Pagination
              page={pagination.page}
              limit={pagination.limit}
              total={pagination.total}
              onPageChange={(page) => onPageChange?.(page)}
            />
          </>
        )}
      </div>

      <AdminCommandDialog
        open={Boolean(processTarget)}
        title={t(forceMode ? 'withdrawals.actions.forceTitle' : 'withdrawals.actions.approveTitle')}
        description={t(forceMode ? 'withdrawals.actions.forceDescription' : 'withdrawals.actions.approveDescription')}
        busy={isActionLoading}
        reasonRequired={forceMode}
        confirmLabel={t(forceMode ? 'withdrawals.actions.force' : 'withdrawals.actions.approve')}
        onOpenChange={(open) => {
          if (!open) {
            setProcessTarget(null);
            setForceMode(false);
          }
        }}
        onConfirm={handleConfirmProcess}
      />
      <PolicyDecisionsDialog withdrawalId={policyTarget} onOpenChange={(open) => !open && setPolicyTarget(null)} />

      <Dialog
        open={Boolean(cancelTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setCancelTarget(null);
            setCancelComment('');
            setFormError(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('withdrawals.actions.adminCancelTitle')}</DialogTitle>
            <DialogDescription>{t('withdrawals.actions.adminCancelDescription')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="cancel-comment">{t('withdrawals.actions.commentLabel')}</Label>
            <Textarea
              id="cancel-comment"
              rows={4}
              value={cancelComment}
              placeholder={t('withdrawals.actions.commentPlaceholder')}
              onChange={(event) => setCancelComment(event.target.value)}
            />
            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)}>
              {t('withdrawals.actions.back')}
            </Button>
            <Button variant="destructive" loading={isActionLoading} onClick={() => void handleConfirmCancel()}>
              {t('withdrawals.actions.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReconcileDialog
        open={Boolean(reconcileTarget)}
        loading={isActionLoading}
        onOpenChange={(open) => {
          if (!open) setReconcileTarget(null);
        }}
        onConfirm={async (reason) => {
          if (!reconcileTarget || reconcileTarget.stateVersion == null || !onReconcile) return;
          const result = await onReconcile(reconcileTarget.id, reason, reconcileTarget.stateVersion);
          if (result.success) {
            toast(t('withdrawals.reconcile.success'), 'success');
            setReconcileTarget(null);
            onRefresh?.();
            return;
          }
          if (result.error) toast(result.error, 'error');
          onRefresh?.();
        }}
      />

      <EmergencyGrantDialog
        withdrawal={emergencyTarget}
        busy={isActionLoading}
        onOpenChange={(open) => !open && setEmergencyTarget(null)}
        onCreateGrant={onCreateGrant}
        onRevokeGrant={onRevokeGrant}
      />
    </>
  );
}

function WithdrawalRowActions({
  withdrawal,
  actorId,
  canExecute,
  canReject,
  canEmergency,
  canReadAudit,
  isActionLoading,
  onProcess,
  onForce,
  onPolicy,
  onCancel,
  onEmergency,
  onReconcile,
}: {
  withdrawal: Withdrawal;
  actorId?: string;
  canExecute: boolean;
  canReject: boolean;
  canEmergency: boolean;
  canReadAudit: boolean;
  isActionLoading: boolean;
  onProcess: () => void;
  onForce: () => void;
  onPolicy: () => void;
  onCancel: () => void;
  onEmergency: () => void;
  onReconcile?: () => void;
}) {
  const { t } = useTranslation('rbac');
  const block = withdrawalApprovalBlock(withdrawal, actorId);
  const approve = canExecute && canApproveAdminWithdrawal(withdrawal, actorId);
  const force = approve && canForceAddressHold(withdrawal);
  const cancel = canReject && canCancelAdminWithdrawal(withdrawal);
  const emergency = canEmergency && canIssueEmergencyGrant(withdrawal);
  const hint = block === 'self_approval'
    ? t('withdrawal.selfApproval')
    : block === 'own_funds'
      ? t('withdrawal.ownFunds')
      : block === 'maker_unknown'
        ? t('withdrawal.unknownMaker')
        : block === 'automatic'
          ? t('withdrawal.automatic')
          : block === 'not_awaiting'
            ? t(merchantHintKey(withdrawal.state, withdrawal.reasonCode), { ns: 'cabinet' })
            : null;

  if (!approve && !cancel && !emergency && !onReconcile) {
    return hint ? <p className="max-w-[16rem] text-xs text-muted-foreground">{hint}</p> : (
      <span className="inline-flex min-h-8 items-center font-sans text-sm text-muted-foreground">{t('withdrawals.actions.none', { ns: 'cabinet' })}</span>
    );
  }

  return (
    <div className="flex min-w-[10rem] flex-col gap-2">
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {approve ? (
          <Button size="sm" variant="default" disabled={isActionLoading} onClick={onProcess}>
            {t('withdrawals.actions.approve', { ns: 'cabinet' })}
          </Button>
        ) : null}
        {force ? (
          <Button size="sm" variant="outline" disabled={isActionLoading} onClick={onForce}>
            {t('withdrawals.actions.force', { ns: 'cabinet' })}
          </Button>
        ) : null}
        <Button size="sm" variant="outline" onClick={onPolicy}>
          {t('withdrawals.policyHistory.open', { ns: 'cabinet' })}
        </Button>
        {onReconcile ? (
          <Button size="sm" variant="outline" disabled={isActionLoading} onClick={onReconcile}>
            {t('withdrawals.reconcile.action', { ns: 'cabinet' })}
          </Button>
        ) : null}
        {cancel ? (
          <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" disabled={isActionLoading} onClick={onCancel}>
            {t('withdrawals.actions.cancel', { ns: 'cabinet' })}
          </Button>
        ) : null}
        {emergency ? (
          <Button size="sm" variant="outline" disabled={isActionLoading} onClick={onEmergency}>
            {t('withdrawal.emergency')}
          </Button>
        ) : null}
        {canReadAudit ? (
          <Button size="sm" variant="outline" asChild>
            <Link to={`/audit-log?targetType=withdrawal&targetId=${encodeURIComponent(withdrawal.id)}`}>
              {t('withdrawal.audit')}
            </Link>
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function EmergencyGrantDialog({
  withdrawal,
  busy,
  onOpenChange,
  onCreateGrant,
  onRevokeGrant,
}: {
  withdrawal: {
    id: string;
    amount: string;
    assetCode: string;
    network: string;
    address: string;
  } | null;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateGrant?: AdminWithdrawalsTableProps['onCreateGrant'];
  onRevokeGrant?: AdminWithdrawalsTableProps['onRevokeGrant'];
}) {
  const { t } = useTranslation('rbac');
  const { toast } = useToast();
  const idempotencyKey = useRef(createIdempotencyKey());
  const [overrides, setOverrides] = useState<EmergencyOverride[]>([]);
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const grants = useEmergencyGrants(withdrawal?.id ?? null);

  const toggle = (value: EmergencyOverride) => {
    setOverrides((current) => current.includes(value) ? current.filter((item) => item !== value) : [...current, value]);
  };

  return (
    <AdminCommandDialog
      open={Boolean(withdrawal)}
      title={t('withdrawal.emergency')}
      description={t('withdrawal.emergencyHint')}
      busy={busy}
      onOpenChange={(open) => {
        if (!open) {
          setOverrides([]);
          setRevokeId(null);
        }
        onOpenChange(open);
      }}
      onConfirm={async (command) => {
        if (!withdrawal) return;
        const result = revokeId && onRevokeGrant
          ? await onRevokeGrant({ id: revokeId, command })
          : onCreateGrant && overrides.length
            ? await onCreateGrant({
              withdrawalId: withdrawal.id,
              overrides,
              idempotencyKey: idempotencyKey.current,
              command,
            })
            : null;
        if (!result) return;
        if (result.success) {
          toast(t('command.confirm'), 'success');
          setOverrides([]);
          setRevokeId(null);
          idempotencyKey.current = createIdempotencyKey();
          await grants.mutate();
          onOpenChange(false);
          return;
        }
        if (result.error) toast(result.error, 'error');
      }}
    >
      <div className="space-y-2 text-sm">
        <p className="break-all">{withdrawal?.amount} {withdrawal?.assetCode} · {withdrawal?.network}</p>
        <p className="break-all">{withdrawal?.address}</p>
        {(grants.data?.data ?? []).map((grant) => {
          const view = grantViewStatus(grant);
          return (
            <div key={grant.id} className="rounded-lg border border-border p-2">
              <p>{t(`withdrawal.grantStatus.${view}`, { time: formatDateTime(grant.expiresAt) })}</p>
              <p className="break-all text-muted-foreground">{grant.overrides.join(', ')} · {grant.reason}</p>
              {canRevokeEmergencyGrant(grant) && onRevokeGrant ? (
                <label className="mt-1 flex items-center gap-2">
                  <input
                    type="radio"
                    name="revoke-grant"
                    checked={revokeId === grant.id}
                    onChange={() => setRevokeId(grant.id)}
                  />
                  {t('withdrawal.revokeGrant')}
                </label>
              ) : null}
            </div>
          );
        })}
        <p className="font-medium">{t('withdrawal.overrides')}</p>
        {([
          ['AML', 'withdrawal.overrideAml'],
          ['ADDRESS_HOLD', 'withdrawal.overrideAddress'],
          ['WITHDRAWAL_LOCK', 'withdrawal.overrideLock'],
        ] as const).map(([value, key]) => (
          <label key={value} className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={overrides.includes(value)}
              onChange={() => toggle(value)}
            />
            {t(key)}
          </label>
        ))}
      </div>
    </AdminCommandDialog>
  );
}
